import { Link } from "react-router-dom";
import {
  Activity,
  Anchor,
  ArrowRight,
  Baby,
  Bone,
  Braces,
  CircleDot,
  Crown,
  HeartPulse,
  Layers,
  LayoutGrid,
  ScanLine,
  Smile,
  Sparkles,
  Syringe,
} from "lucide-react";
import { SERVICES, TECH_ITEMS } from "../config";
import { Reveal, SectionLabel } from "../components/Reveal";

const ICONS = {
  Activity, Smile, Sparkles, Layers, CircleDot, Crown, Braces, Anchor,
  Baby, LayoutGrid, ScanLine, Syringe, HeartPulse, Bone,
};

export default function Services() {
  return (
    <main>
      <section className="relative overflow-hidden bg-[#14717A] pb-16 pt-32 sm:pb-20 sm:pt-40">
        <div className="pointer-events-none absolute -right-24 -top-24 h-96 w-96 rounded-full bg-[#4CA6AF]/25 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-32 -left-16 h-80 w-80 rounded-full bg-[#0d5259]/60 blur-3xl" />
        <div className="relative mx-auto max-w-7xl px-6 sm:px-8">
          <Reveal>
            <p className="text-xs font-bold tracking-[0.3em] uppercase text-[#9fd8dd]">Our Treatments</p>
            <h1 data-testid="services-title" className="mt-4 font-heading text-3xl font-semibold text-white sm:text-4xl">
              What We Offer
            </h1>
          </Reveal>
        </div>
      </section>

      <section className="bg-[#f7fafb] py-12 sm:py-16" data-testid="services-grid-section">
        <div className="mx-auto max-w-7xl px-6 sm:px-8">
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {SERVICES.map((s, i) => {
              const Icon = ICONS[s.icon] || Activity;
              return (
                <Reveal key={s.slug} delay={(i % 3) * 0.08}>
                  <div
                    id={`service-${s.slug}`}
                    data-testid={`service-card-${s.slug}`}
                    className="group flex h-full items-start gap-4 rounded-3xl border border-[#4CA6AF]/30 border-l-[6px] border-l-[#14717A] bg-white p-5 shadow-[0_0_0_3px_rgba(76,166,175,0.12),0_14px_34px_rgba(76,166,175,0.22)] transition-all duration-500 hover:-translate-y-1.5 hover:shadow-[0_0_0_4px_rgba(76,166,175,0.2),0_20px_44px_rgba(76,166,175,0.3)] sm:p-6"
                  >
                    <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-[#f2f6f7] text-[#14717A] transition-colors duration-500 group-hover:bg-[#14717A] group-hover:text-white">
                      <Icon className="h-5 w-5" />
                    </div>
                    <div>
                      <h2 className="font-sans text-lg font-semibold text-[#14717A]">{s.title}</h2>
                      <p className="mt-1.5 text-sm leading-relaxed text-[#8A9CAB]">{s.desc}</p>
                    </div>
                  </div>
                </Reveal>
              );
            })}
          </div>

          <Reveal className="mt-16">
            <h2 className="font-sans text-xl font-light text-[#14717A] sm:text-2xl" data-testid="tech-title">
              Advanced Dental Technology
            </h2>
          </Reveal>
          <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {TECH_ITEMS.map((t, i) => (
              <Reveal key={t.name} delay={(i % 3) * 0.08}>
                <div
                  data-testid={`tech-card-${i}`}
                  className="group h-full rounded-3xl border border-[#4CA6AF]/30 border-l-[6px] border-l-[#14717A] bg-white p-4 shadow-[inset_10px_0_16px_-10px_rgba(20,113,122,0.5),0_0_0_3px_rgba(76,166,175,0.12),0_14px_34px_rgba(76,166,175,0.22)] transition-all duration-500 hover:-translate-y-1.5 hover:shadow-[inset_10px_0_16px_-10px_rgba(20,113,122,0.5),0_0_0_4px_rgba(76,166,175,0.2),0_20px_44px_rgba(76,166,175,0.3)]"
                >
                  <h3 className="font-sans text-base font-bold text-[#14717A]">{t.name}</h3>
                  <img
                    src={t.src}
                    alt={t.name}
                    data-testid={`tech-image-${i}`}
                    loading="lazy"
                    className="mt-3 h-32 w-full rounded-2xl object-cover transition-transform duration-700 group-hover:scale-[1.03] sm:h-36"
                  />
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal className="mt-16">
            <div className="flex flex-col items-center justify-between gap-6 rounded-3xl bg-[#14717A] p-10 text-center sm:flex-row sm:text-left">
              <div>
                <h3 className="font-heading text-2xl font-semibold text-white">Not sure which treatment you need?</h3>
                <p className="mt-2 text-sm text-white/75">
                  Book a consultation — we'll examine, explain and plan together.
                </p>
              </div>
              <Link
                to="/book"
                data-testid="services-book-btn"
                className="group inline-flex shrink-0 items-center gap-2 rounded-full bg-[#4CA6AF] px-8 py-4 text-sm font-bold text-white transition-all duration-300 hover:-translate-y-0.5 hover:bg-white hover:text-[#14717A]"
              >
                Book Appointment
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
